import { useCallback } from "react";
import type { KeyValue, MapObject } from "@fortex/api";
import type { InfoState } from "../../core/store";
import { format } from "../../i18n/labels";
import { useInfo, useIntl, useLabels, useMapCore, usePanel } from "../hooks";
import { Icon } from "../icons";
import { objectTitle } from "../fieldValues";
import { Button } from "../primitives/Button";
import { Drawer } from "../primitives/Drawer";
import { EmptyState } from "../primitives/EmptyState";
import { Skeleton } from "../primitives/Skeleton";
import { isObjectDetail, ObjectCard } from "./ObjectCard";
import { TileFeaturePanel } from "./TileFeaturePanel";

export type FeatureInspectorProps = {
  variant?: "panel" | "popup" | "embedded";
  pinnable?: boolean;
  className?: string;
};

export function inspectorTitle(state: InfoState, labels: { featureTitle: string; info: string; members: string; featureChooser: string; selectedCount: string }): string {
  switch (state.kind) {
    case "object":
      return objectTitle(state.object);
    case "tile":
      return state.feature.hit.name.length > 0 ? state.feature.hit.name : labels.featureTitle;
    case "multi":
      return format(labels.selectedCount, { count: state.objects.length });
    case "cluster":
      return format(labels.members, { count: state.feature.cluster?.count ?? 0 });
    default:
      return labels.info;
  }
}

function ObjectList({ objects, onPick }: { objects: MapObject[]; onPick: (object: MapObject) => void }) {
  return (
    <ul className="fxmap-object-list" role="list">
      {objects.map(object => (
        <li key={object.id} role="listitem">
          <button type="button" className="fxmap-object-list-item" onClick={() => onPick(object)} data-fxmap-object={object.id}>
            <span className="fxmap-object-list-text">
              <span className="fxmap-object-list-name fxmap-truncate">{objectTitle(object)}</span>
              {object.typeName ? <span className="fxmap-object-list-type fxmap-muted fxmap-truncate">{object.typeName}</span> : null}
            </span>
            <Icon name="chevronRight" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function FeatureInspectorBody({ onClose }: { onClose: (() => void) | null }) {
  const core = useMapCore();
  const labels = useLabels();
  const intl = useIntl();
  const { state, api } = useInfo();

  const saveFeature = useCallback((data: KeyValue[]) => void api.saveFeature(data), [api]);
  const markDirty = useCallback(
    (dirty: boolean) => {
      const current = core.store.getState().info;

      if (current.kind === "tile" && current.dirty !== dirty) core.store.getState().setInfo({ ...current, dirty });
    },
    [core]
  );

  switch (state.kind) {
    case "closed":
      return <EmptyState icon="info" title={labels.noSelection} />;
    case "loading":
      return (
        <div className="fxmap-inspector-loading" aria-busy="true" aria-label={labels.loading}>
          <Skeleton shape="block" height={96} />
          <Skeleton lines={3} />
        </div>
      );
    case "object":
      return <ObjectCard object={state.object} detail={isObjectDetail(state.detail) ? state.detail : null} loading={state.detail === null && state.error === null} error={state.error} onClose={onClose} />;
    case "cluster": {
      const cluster = state.feature.cluster;
      const counts = Object.entries(cluster?.typeCounts ?? {});

      return (
        <div className="fxmap-cluster-card" data-fxmap="cluster-card">
          <p className="fxmap-cluster-count fxmap-display">{format(labels.members, { count: cluster?.count ?? 0 })}</p>
          {counts.length > 0 ? (
            <ul className="fxmap-cluster-types" role="list">
              {counts.map(([type, count]) => (
                <li key={type} className="fxmap-cluster-type" role="listitem">
                  <span className="fxmap-truncate">{type}</span>
                  <span className="fxmap-tabular fxmap-muted">{intl.integer(count)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <Button kind="ghost" size="sm" icon="plus" label={labels.zoomIn} action="zoom-in" onClick={() => core.view.setView(state.feature.anchor, core.store.getState().view.zoom + 2)} />
        </div>
      );
    }
    case "multi":
      return <ObjectList objects={state.objects} onPick={object => api.openObject(object)} />;
    case "tile":
      return <TileFeaturePanel key={`${state.feature.hit.layer}:${state.feature.hit.id}`} feature={state.feature} saving={state.saving} error={state.error} searchTerm={state.searchTerm ?? null} onSave={saveFeature} onDirtyChange={markDirty} onClose={onClose} />;
    case "error":
      return <EmptyState icon="warning" title={labels.featureLoadFailed} text={state.message} action={onClose !== null ? <Button kind="ghost" label={labels.close} onClick={onClose} /> : undefined} />;
    default:
      return null;
  }
}

export function FeatureInspector({ variant = "panel", pinnable = false, className }: FeatureInspectorProps) {
  const core = useMapCore();
  const labels = useLabels();
  const { state, api } = useInfo();
  const { pinned } = usePanel();
  const open = state.kind !== "closed";
  const close = () => api.close();

  if (variant === "embedded") {
    return (
      <div className={["fxmap-inspector", "fxmap-inspector-embedded", className ?? ""].filter(Boolean).join(" ")} data-fxmap="inspector">
        <FeatureInspectorBody onClose={null} />
      </div>
    );
  }

  if (!open) return null;

  if (variant === "popup") {
    return (
      <div className={["fxmap-inspector", "fxmap-inspector-popup", "fxmap-card", className ?? ""].filter(Boolean).join(" ")} data-fxmap="inspector" role="dialog" aria-label={inspectorTitle(state, labels)}>
        <FeatureInspectorBody onClose={close} />
      </div>
    );
  }

  const bare = state.kind === "object" || state.kind === "tile";

  return (
    <Drawer
      open
      side="right"
      role="complementary"
      title={inspectorTitle(state, labels)}
      onClose={close}
      closeLabel={labels.close}
      pinned={pinnable ? pinned : null}
      onPin={pinnable ? value => core.store.getState().patch({ panelPinned: value }) : null}
      pinLabel={labels.pinPanel}
      unpinLabel={labels.unpinPanel}
      className={["fxmap-inspector", bare ? "fxmap-inspector-bare" : "", className ?? ""].filter(Boolean).join(" ")}
      dataFxmap="inspector"
    >
      <FeatureInspectorBody onClose={bare ? close : null} />
    </Drawer>
  );
}
